import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, LogOut, X, ImagePlus, ArrowUp, ArrowDown, Loader2, Users } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { FloatingOrbs } from "@/components/landing/FloatingOrbs";

const EMPTY = { title: "Mermaid Melody Live", date: "", time: "", city: "", venue: "", status: "confirmed", ticket_url: "", image: "" };
const inputCls = "rounded-xl border border-navy/10 bg-white/70 px-4 py-3 text-navy outline-none focus:border-lavender transition-colors";

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cs_token", data.token);
      onLogin(data.user);
      toast.success("Bentornato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-[100svh] items-center justify-center px-5">
      <FloatingOrbs />
      <motion.form
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={submit}
        className="relative z-10 w-full max-w-sm rounded-[2rem] glass-strong p-8"
        data-testid="admin-login-form"
      >
        <p className="mb-2 text-xs uppercase tracking-[0.3em] text-lavender-deep">Area riservata</p>
        <h1 className="font-serif text-3xl tracking-tightest mb-8 text-navy">Accedi</h1>
        <label className="block text-sm text-navy-soft mb-2">Email</label>
        <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`mb-5 w-full ${inputCls}`} required />
        <label className="block text-sm text-navy-soft mb-2">Password</label>
        <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`mb-7 w-full ${inputCls}`} required />
        <button data-testid="admin-login-btn" disabled={loading} className="w-full rounded-full bg-lavender py-3 font-medium text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 glow-lav">
          {loading ? "Accesso..." : "Entra"}
        </button>
        <a href="/" className="mt-6 block text-center text-sm text-navy-faint hover:text-lavender-deep transition-colors">← Torna al sito</a>
      </motion.form>
    </div>
  );
}

function EventForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onImage = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/events/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image: data.src }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/30 backdrop-blur-sm px-5 py-8 overflow-y-auto" data-testid="event-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg rounded-[2rem] glass-strong p-8">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-serif text-2xl text-navy">{initial?.id ? "Modifica evento" : "Nuovo evento"}</h3>
          <button data-testid="event-modal-close" onClick={onClose} className="text-navy-faint hover:text-navy"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <input data-testid="event-title-input" placeholder="Titolo" value={form.title} onChange={set("title")} className={`col-span-2 ${inputCls}`} />
          <input data-testid="event-date-input" type="date" value={form.date} onChange={set("date")} className={inputCls} />
          <input data-testid="event-time-input" type="time" value={form.time} onChange={set("time")} className={inputCls} />
          <input data-testid="event-city-input" placeholder="Città" value={form.city} onChange={set("city")} className={inputCls} />
          <input data-testid="event-venue-input" placeholder="Location / Convention" value={form.venue} onChange={set("venue")} className={inputCls} />
          <select data-testid="event-status-input" value={form.status} onChange={set("status")} className={inputCls}>
            <option value="confirmed">Confermato</option>
            <option value="soldout">Sold out</option>
            <option value="announced">In arrivo</option>
          </select>
          <input data-testid="event-ticket-input" placeholder="Link biglietti / info (opzionale)" value={form.ticket_url} onChange={set("ticket_url")} className={inputCls} />
        </div>

        <div className="mt-5">
          <label className="block text-sm text-navy-soft mb-2">Immagine della fiera (opzionale)</label>
          {form.image ? (
            <div className="relative overflow-hidden rounded-2xl glass p-1.5">
              <img src={mediaUrl(form.image)} alt="Anteprima immagine evento" className="h-40 w-full rounded-xl object-cover" data-testid="event-image-preview" />
              <button
                type="button"
                data-testid="event-image-remove"
                onClick={() => setForm((prev) => ({ ...prev, image: "" }))}
                className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-navy-soft hover:text-red-500 backdrop-blur"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full glass px-5 py-2.5 text-sm font-medium text-navy hover:text-lavender-deep transition-colors">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
              {uploading ? "Caricamento…" : "Carica immagine"}
              <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={onImage} disabled={uploading} data-testid="event-image-upload" />
            </label>
          )}
        </div>

        <button data-testid="event-save-btn" onClick={() => onSave(form)} disabled={uploading} className="mt-7 w-full rounded-full bg-lavender py-3 font-medium text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60">
          Salva evento
        </button>
      </motion.div>
    </div>
  );
}

function PhotoManager({ section }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/photos?section=${section}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [section]);

  const onFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("section", section);
    setUploading(true);
    try {
      await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto caricata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    await api.delete(`/photos/${id}`);
    toast.success("Foto eliminata");
    load();
  };

  const move = async (id, direction) => {
    await api.post(`/photos/${id}/move`, { direction });
    load();
  };

  return (
    <div data-testid={`photo-manager-${section}`}>
      <label className="mb-6 inline-flex cursor-pointer items-center gap-2 rounded-full bg-lavender px-5 py-2.5 text-sm font-medium text-white hover:scale-[1.03] transition-transform glow-lav">
        {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
        {uploading ? "Caricamento…" : "Carica foto"}
        <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={onFile} disabled={uploading} data-testid={`photo-upload-${section}`} />
      </label>

      {items.length === 0 && <p className="py-8 text-navy-faint">Nessuna foto. Caricane una.</p>}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((p, i) => (
          <div key={p.id} className="group relative overflow-hidden rounded-2xl glass p-1.5" data-testid={`photo-item-${section}-${i}`}>
            <img src={mediaUrl(p.src)} alt="Foto della galleria" className="aspect-square w-full rounded-xl object-cover" />
            <div className="absolute inset-x-1.5 bottom-1.5 flex items-center justify-between gap-1 rounded-full bg-white/85 px-2 py-1 backdrop-blur transition-opacity opacity-0 group-hover:opacity-100">
              <div className="flex gap-1">
                <button onClick={() => move(p.id, "up")} disabled={i === 0} className="rounded-full p-1.5 text-navy-soft hover:text-lavender-deep disabled:opacity-30" data-testid={`photo-up-${section}-${i}`}><ArrowUp size={15} /></button>
                <button onClick={() => move(p.id, "down")} disabled={i === items.length - 1} className="rounded-full p-1.5 text-navy-soft hover:text-lavender-deep disabled:opacity-30" data-testid={`photo-down-${section}-${i}`}><ArrowDown size={15} /></button>
              </div>
              <button onClick={() => del(p.id)} className="rounded-full p-1.5 text-navy-soft hover:text-red-500" data-testid={`photo-delete-${section}-${i}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CommunityManager() {
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/community").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div data-testid="community-manager">
      <div className="mb-6 inline-flex items-center gap-3 rounded-2xl glass px-5 py-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-lavender/15 text-lavender-deep"><Users size={20} /></span>
        <div>
          <p className="text-2xl font-serif text-navy leading-none" data-testid="community-count">{data.count}</p>
          <p className="text-sm text-navy-faint">iscritti alla community</p>
        </div>
      </div>

      {loading ? (
        <p className="py-8 text-navy-faint">Caricamento…</p>
      ) : data.items.length === 0 ? (
        <p className="py-8 text-navy-faint">Nessun iscritto ancora.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl glass">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-navy/10 text-navy-faint">
                <th className="px-5 py-3 font-medium">Nome</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">CAP</th>
                <th className="px-5 py-3 font-medium">Data</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c, i) => (
                <tr key={c.id} className="border-b border-navy/5 last:border-0" data-testid={`community-row-${i}`}>
                  <td className="px-5 py-3 text-navy">{c.nome}</td>
                  <td className="px-5 py-3 text-navy-soft">{c.email}</td>
                  <td className="px-5 py-3 text-navy-soft">{c.cap}</td>
                  <td className="px-5 py-3 text-navy-faint">{fmt(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState("events");

  const load = () => api.get("/events").then((r) => setEvents(r.data));

  useEffect(() => {
    const token = localStorage.getItem("cs_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then((r) => { setUser(r.data); load(); }).catch(() => localStorage.removeItem("cs_token")).finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem("cs_token"); setUser(null); };

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/events/${form.id}`, form);
      else await api.post("/events", form);
      toast.success("Evento salvato");
      setShowForm(false); setEditing(null);
      load();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo evento?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Evento eliminato");
    load();
  };

  if (checking) return <div className="flex min-h-[100svh] items-center justify-center text-navy-faint">Caricamento…</div>;
  if (!user) return <LoginView onLogin={(u) => { setUser(u); load(); }} />;

  return (
    <div className="min-h-[100svh] px-5 sm:px-8 lg:px-12 py-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-lavender-deep mb-1">Pannello</p>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tightest text-navy">Dashboard</h1>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full glass px-5 py-2.5 text-sm text-navy hover:text-lavender-deep transition-colors">
            <LogOut size={16} /> Esci
          </button>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {[["events", "Eventi"], ["gallery", "Galleria community"], ["fans", "Foto con i fan"], ["subscribers", "Community"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`tab-${k}`}
              onClick={() => setTab(k)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === k ? "bg-navy text-white" : "glass text-navy hover:text-lavender-deep"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "events" && (
          <div>
            <button data-testid="admin-add-btn" onClick={() => { setEditing(EMPTY); setShowForm(true); }} className="mb-6 inline-flex items-center gap-2 rounded-full bg-lavender px-5 py-2.5 text-sm font-medium text-white hover:scale-[1.03] transition-transform glow-lav">
              <Plus size={16} /> Nuovo evento
            </button>
            <div className="flex flex-col gap-3">
              {events.length === 0 && <p className="py-10 text-navy-faint">Nessun evento. Aggiungine uno.</p>}
              {events.map((ev, i) => (
                <div key={ev.id} className="flex items-center justify-between gap-4 rounded-2xl glass px-6 py-5" data-testid={`admin-event-${i}`}>
                  <div className="min-w-0">
                    <p className="text-lg text-navy truncate">{ev.venue} <span className="text-navy-faint">· {ev.city}</span></p>
                    <p className="text-sm text-navy-faint">{ev.date} {ev.time && `· ${ev.time}`} · {ev.status}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`admin-edit-${i}`} onClick={() => { setEditing(ev); setShowForm(true); }} className="rounded-full bg-white/70 p-2.5 text-navy-soft hover:bg-lavender hover:text-white transition-colors"><Pencil size={16} /></button>
                    <button data-testid={`admin-delete-${i}`} onClick={() => remove(ev.id)} className="rounded-full bg-white/70 p-2.5 text-navy-soft hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && <PhotoManager section="gallery" />}
        {tab === "fans" && <PhotoManager section="fans" />}
        {tab === "subscribers" && <CommunityManager />}
      </div>

      {showForm && <EventForm initial={editing} onSave={save} onClose={() => { setShowForm(false); setEditing(null); }} />}
    </div>
  );
}
